import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CancelIcon from '@material-ui/icons/Cancel'
import EditIcon from '@material-ui/icons/Edit'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import axios from 'axios'

// ── Enums (mirroring model) ────────────────────────────────────────────────────

const AGGREGATE_TYPES = [
  'SCREENED',
  'MINUS',
  'RIP_RAP',
  'WASHED',
  'SAND',
  'ABC',
  'DECO',
  'BOULDER',
  'FLAGSTONE',
  'INFIELD_MIX',
  'PLAYGROUND_SURFACE',
  'SOIL_OR_PLANTING_MIX',
  'UNKNOWN'
]

const UNIT_OPTIONS = ['TON', 'EACH', 'LB', 'SQFT', 'CY', 'UNKNOWN']

// ── Type chip color map ────────────────────────────────────────────────────────

const TYPE_COLORS = {
  SCREENED: { bg: '#e3f2fd', color: '#1565c0' },
  MINUS: { bg: '#f3e5f5', color: '#6a1b9a' },
  RIP_RAP: { bg: '#fff3e0', color: '#e65100' },
  WASHED: { bg: '#e8f5e9', color: '#2e7d32' },
  SAND: { bg: '#fffde7', color: '#f57f17' },
  ABC: { bg: '#fce4ec', color: '#880e4f' },
  DECO: { bg: '#e0f7fa', color: '#006064' },
  BOULDER: { bg: '#efebe9', color: '#4e342e' },
  FLAGSTONE: { bg: '#f1f8e9', color: '#33691e' },
  INFIELD_MIX: { bg: '#fbe9e7', color: '#bf360c' },
  PLAYGROUND_SURFACE: { bg: '#e8eaf6', color: '#283593' },
  SOIL_OR_PLANTING_MIX: { bg: '#e0f2f1', color: '#004d40' },
  UNKNOWN: { bg: '#f5f5f5', color: '#757575' }
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  headerCell: {
    padding: theme.spacing(0.75, 1.5),
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  cellCompact: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem'
  },
  actionsCell: {
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap'
  },
  expandedRow: {
    backgroundColor: '#fafafa'
  },
  aliasPanel: {
    padding: theme.spacing(1.5, 2, 2, 4),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  aliasList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    alignItems: 'center',
    marginTop: theme.spacing(0.75)
  },
  aliasChip: {
    backgroundColor: '#eeeeee',
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24
  },
  addAliasInput: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1),
    height: 28,
    fontSize: '0.78rem',
    width: 150,
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main
    }
  },
  aliasError: {
    color: '#c62828',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5)
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  dialogField: {
    marginBottom: theme.spacing(1.5)
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5)
  }
}))

// ── Empty create form ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
  displayName: '',
  aggregateType: 'UNKNOWN',
  defaultUnit: 'TON'
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function GradationsTab () {
  const classes = useStyles()

  const [gradations, setGradations] = useState([])
  const [loading, setLoading] = useState(true)

  // Expanded row state: tracks which row's _id is expanded (only one at a time)
  const [expandedId, setExpandedId] = useState(null)

  // Per-row alias input state: { [gradationId]: { inputValue, saving, error } }
  const [aliasState, setAliasState] = useState({})

  // Filters
  const [searchText, setSearchText] = useState('')
  const [filterType, setFilterType] = useState('')
  const [sortBy, setSortBy] = useState('size') // 'size' or 'name'

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false)
  const [seedLoading, setSeedLoading] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_FORM)
  const [createSaving, setCreateSaving] = useState(false)
  const [createError, setCreateError] = useState('')

  // ── Fetch gradations ──────────────────────────────────────────────────────────
  const fetchGradations = useCallback(() => {
    setLoading(true)
    const params = { sort: sortBy }
    if (filterType) params.aggregateType = filterType
    if (searchText.trim()) params.search = searchText.trim()
    axios.get('/api/aggregate-dashboard/gradations', { params })
      .then((res) => setGradations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[GradationsTab] fetch error:', err))
      .finally(() => setLoading(false))
  }, [sortBy, filterType, searchText])

  useEffect(() => {
    fetchGradations()
  }, [fetchGradations])

  // ── Row expand toggle ─────────────────────────────────────────────────────────
  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id))
    // Reset alias input state for this row on expand
    setAliasState((prev) => ({
      ...prev,
      [id]: { inputValue: '', saving: false, error: '' }
    }))
  }

  // ── Alias management ─────────────────────────────────────────────────────────
  const getAliasRow = (id) => aliasState[id] || { inputValue: '', saving: false, error: '' }

  const setAliasField = (id, field, value) => {
    setAliasState((prev) => ({
      ...prev,
      [id]: { ...getAliasRow(id), ...prev[id], [field]: value }
    }))
  }

  const handleAliasKeyDown = async (e, gradation) => {
    if (e.key !== 'Enter') return
    const id = gradation._id
    const row = getAliasRow(id)
    const alias = row.inputValue.trim()
    if (!alias) return

    setAliasField(id, 'saving', true)
    setAliasField(id, 'error', '')

    const updatedAliases = [...(gradation.aliases || []), alias]

    try {
      const res = await axios.patch(`/api/aggregate-dashboard/gradations/${id}`, { aliases: updatedAliases })
      // Update local gradation list with returned data
      setGradations((prev) => prev.map((g) => g._id === id ? { ...g, aliases: res.data.aliases || updatedAliases } : g))
      setAliasField(id, 'inputValue', '')
      setAliasField(id, 'saving', false)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setAliasState((prev) => ({
        ...prev,
        [id]: { ...getAliasRow(id), ...prev[id], saving: false, error: msg }
      }))
    }
  }

  const handleRemoveAlias = async (gradation, aliasToRemove) => {
    const id = gradation._id
    const updatedAliases = (gradation.aliases || []).filter((a) => a !== aliasToRemove)

    try {
      const res = await axios.patch(`/api/aggregate-dashboard/gradations/${id}`, { aliases: updatedAliases })
      setGradations((prev) => prev.map((g) => g._id === id ? { ...g, aliases: res.data.aliases || updatedAliases } : g))
    } catch (err) {
      console.error('[GradationsTab] remove alias error:', err)
    }
  }

  // ── Create dialog handlers ────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setCreateForm(EMPTY_FORM)
    setCreateError('')
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    if (createSaving) return
    setCreateOpen(false)
    setCreateForm(EMPTY_FORM)
    setCreateError('')
  }

  const handleCreateChange = (field) => (e) => {
    setCreateForm((f) => ({ ...f, [field]: e.target.value }))
  }

  const handleSubmitCreate = async () => {
    if (!createForm.displayName.trim()) { setCreateError('Display Name is required'); return }
    if (!createForm.aggregateType) { setCreateError('Aggregate Type is required'); return }

    setCreateSaving(true)
    setCreateError('')
    try {
      await axios.post('/api/aggregate-dashboard/gradations', {
        displayName: createForm.displayName.trim(),
        aggregateType: createForm.aggregateType,
        defaultUnit: createForm.defaultUnit || 'TON'
      })
      setCreateOpen(false)
      setCreateForm(EMPTY_FORM)
      fetchGradations()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreateSaving(false)
    }
  }

  // ── Computed ───────────────────────────────────────────────────────────────────
  const filteredGradations = (showAll ? gradations : gradations.filter(g => {
    if (g.aggregateType === 'UNKNOWN') return false
    if (/weight|item\.no/i.test(g.displayName)) return false
    return true
  })).sort((a, b) => {
    if (sortBy === 'name') {
      // True alphabetical: letters before numbers
      const aIsLetter = /^[a-zA-Z]/.test(a.displayName)
      const bIsLetter = /^[a-zA-Z]/.test(b.displayName)
      if (aIsLetter && !bIsLetter) return -1
      if (!aIsLetter && bIsLetter) return 1
      return a.displayName.localeCompare(b.displayName)
    }
    // Size sort: by sizeInches (nulls last), then sortOrder
    const aSize = a.sizeInches != null ? a.sizeInches : 9999
    const bSize = b.sizeInches != null ? b.sizeInches : 9999
    if (aSize !== bSize) return aSize - bSize
    return (a.sortOrder || 0) - (b.sortOrder || 0)
  })
  const hiddenGradationCount = gradations.length - filteredGradations.length

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.tableHeader}>
        <Typography variant="h6">Gradations</Typography>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              setSeedLoading(true)
              try {
                const res = await axios.post('/api/aggregate-dashboard/seed-gradations')
                const { created, updated, skipped, backfilled, deduped } = res.data
                alert(`Seed complete: ${created} created, ${updated} updated, ${skipped} unchanged, ${backfilled || 0} sizes backfilled, ${deduped || 0} duplicates merged`)
                fetchGradations()
              } catch (err) {
                alert('Seed error: ' + (err.response?.data?.error || err.message))
              } finally {
                setSeedLoading(false)
              }
            }}
            disabled={seedLoading}
            style={{ textTransform: 'none', fontSize: 12 }}
          >
            {seedLoading ? 'Seeding...' : 'Seed Standard Gradations'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Gradation
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search gradations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 200 }}
        />
        <TextField
          select
          label="Type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 150 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="MINUS">Minus</MenuItem>
          <MenuItem value="SCREENED">Screened</MenuItem>
          <MenuItem value="WASHED">Washed</MenuItem>
          <MenuItem value="RIP_RAP">Rip Rap</MenuItem>
          <MenuItem value="SAND">Sand</MenuItem>
          <MenuItem value="ABC">ABC</MenuItem>
          <MenuItem value="DECO">Deco / DG</MenuItem>
          <MenuItem value="BOULDER">Boulder</MenuItem>
          <MenuItem value="FLAGSTONE">Flagstone</MenuItem>
          <MenuItem value="SOIL_OR_PLANTING_MIX">Soil / Planting Mix</MenuItem>
        </TextField>
        <TextField
          select
          label="Sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 140 }}
        >
          <MenuItem value="size">Size (small → large)</MenuItem>
          <MenuItem value="name">Name (A → Z)</MenuItem>
        </TextField>
      </div>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress size={32} />
        </Box>
      ) : filteredGradations.length === 0 ? (
        <Paper variant="outlined">
          <div className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No gradations found. Add one to get started.
            </Typography>
          </div>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell} style={{ width: 32 }} />
                <TableCell className={classes.headerCell}>Display Name</TableCell>
                <TableCell className={classes.headerCell}>Size</TableCell>
                <TableCell className={classes.headerCell}>Type</TableCell>
                <TableCell className={classes.headerCell}>Default Unit</TableCell>
                <TableCell className={classes.headerCell}>Aliases</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGradations.map((g) => {
                const isExpanded = expandedId === g._id
                const typeColor = TYPE_COLORS[g.aggregateType] || TYPE_COLORS.UNKNOWN
                const aliasCount = (g.aliases || []).length
                const row = getAliasRow(g._id)

                return (
                  <React.Fragment key={g._id}>
                    <TableRow
                      hover
                      onClick={() => handleToggleExpand(g._id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell className={classes.cellCompact} style={{ width: 32, paddingLeft: 8 }}>
                        {isExpanded
                          ? <ExpandLessIcon fontSize="small" style={{ color: '#757575', display: 'block' }} />
                          : <ExpandMoreIcon fontSize="small" style={{ color: '#757575', display: 'block' }} />}
                      </TableCell>
                      <TableCell className={classes.cellCompact}>
                        <strong>{g.displayName}</strong>
                        {g.sizeLabel && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            {g.sizeLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.cellCompact} style={{ color: '#888', fontSize: 12 }}>
                        {g.sizeMinInches != null && g.sizeMaxInches != null && g.sizeMinInches !== g.sizeMaxInches
                          ? g.sizeMinInches + '" – ' + g.sizeMaxInches + '"'
                          : g.sizeInches != null ? g.sizeInches + '"' : g.sizeLabel || '—'}
                        {g.estimatedTonsPerUnit ? <span style={{ display: 'block', fontSize: 10, color: '#4caf50' }}>~{g.estimatedTonsPerUnit}T ea</span> : null}
                      </TableCell>
                      <TableCell className={classes.cellCompact}>
                        <Chip
                          label={g.aggregateType || 'UNKNOWN'}
                          size="small"
                          style={{
                            backgroundColor: typeColor.bg,
                            color: typeColor.color,
                            fontWeight: 600,
                            fontSize: '0.68rem',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.cellCompact}>
                        {g.defaultUnit || 'TON'}
                      </TableCell>
                      <TableCell className={classes.cellCompact}>
                        <Typography variant="body2" color="textSecondary">
                          {aliasCount === 0 ? '—' : aliasCount}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.actionsCell} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Expand to manage aliases">
                          <IconButton size="small" onClick={() => handleToggleExpand(g._id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Alias expansion panel */}
                    <TableRow className={isExpanded ? classes.expandedRow : undefined}>
                      <TableCell colSpan={6} style={{ padding: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <div className={classes.aliasPanel}>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Aliases
                            </Typography>
                            <div className={classes.aliasList}>
                              {(g.aliases || []).map((alias) => (
                                <Chip
                                  key={alias}
                                  label={alias}
                                  size="small"
                                  className={classes.aliasChip}
                                  deleteIcon={
                                    <Tooltip title="Remove alias">
                                      <CancelIcon style={{ fontSize: 14 }} />
                                    </Tooltip>
                                  }
                                  onDelete={() => handleRemoveAlias(g, alias)}
                                />
                              ))}
                              {/* Add alias input */}
                              {row.saving ? (
                                <CircularProgress size={16} />
                              ) : (
                                <InputBase
                                  className={classes.addAliasInput}
                                  placeholder="+ add alias"
                                  value={row.inputValue || ''}
                                  onChange={(e) => setAliasField(g._id, 'inputValue', e.target.value)}
                                  onKeyDown={(e) => handleAliasKeyDown(e, g)}
                                  inputProps={{ 'aria-label': 'Add alias', style: { padding: 0 } }}
                                />
                              )}
                            </div>
                            {row.error && (
                              <Typography className={classes.aliasError}>{row.error}</Typography>
                            )}
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                              Press Enter to add an alias. Click × to remove.
                            </Typography>
                          </div>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {hiddenGradationCount > 0 && (
        <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
            {hiddenGradationCount} unclassified item{hiddenGradationCount !== 1 ? 's' : ''} hidden (type=UNKNOWN — likely color names, not gradations)
          </Typography>
          <Button size="small" onClick={() => setShowAll(!showAll)} style={{ textTransform: 'none', fontSize: 11 }}>
            {showAll ? 'Hide' : 'Show All'}
          </Button>
        </Box>
      )}

      {/* Create Gradation Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle>Add Gradation</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 14, paddingTop: 4 }}>
            <TextField
              label="Display Name *"
              value={createForm.displayName}
              onChange={handleCreateChange('displayName')}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. 1/2 Minus Crushed"
              autoFocus
            />

            <TextField
              select
              label="Aggregate Type *"
              value={createForm.aggregateType}
              onChange={handleCreateChange('aggregateType')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {AGGREGATE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Default Unit"
              value={createForm.defaultUnit}
              onChange={handleCreateChange('defaultUnit')}
              variant="outlined"
              size="small"
              fullWidth
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>

            {createError && (
              <Typography className={classes.errorText}>{createError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate} disabled={createSaving}>Cancel</Button>
          <Button
            onClick={handleSubmitCreate}
            color="primary"
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
